import { useState, useCallback, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="employee-list"]',
        title: 'Quản lý nhân sự',
        description:
            'Trang quản lý toàn bộ nhân sự trong hệ thống.\n• Danh sách nhân viên\n• Quản lý phòng ban\n• Thêm/sửa/xoá nhân viên',
    },
    {
        selector: '[data-tour="employee-tabs"]',
        title: 'Chuyển đổi danh sách',
        description:
            'Chuyển đổi giữa các tab:\n• Danh sách nhân viên - xem tất cả nhân viên\n• Phòng ban - quản lý cơ cấu tổ chức',
    },
    {
        selector: '[data-tour="create-employee-btn"]',
        title: 'Thêm nhân viên mới',
        description:
            'Click để tạo nhân viên mới.\n• Nhập thông tin cá nhân\n• Phân công phòng ban\n• Cấp quyền truy cập hệ thống',
        demo: 'create-employee-modal',
    },
    {
        selector: '[data-tour="employee-filter-bar"]',
        title: 'Bộ lọc nâng cao',
        description:
            'Lọc nhân viên theo nhiều tiêu chí:\n• Loại nhân viên (chính thức/thử việc)\n• Giới tính\n• Trạng thái làm việc\n• Ngày sinh\n• Phòng ban & Vị trí',
    },
    {
        selector: '[data-tour="employee-data-table"]',
        title: 'Bảng dữ liệu nhân viên',
        description:
            'Bảng hiển thị danh sách nhân viên với đầy đủ thông tin:\n• Click vào tiêu đề cột để sắp xếp\n• Click vào hành động (...) để chỉnh sửa\n• Phân trang tự động',
        demo: 'employee-actions-dropdown',
    },
    {
        selector: '[data-tour="employee-search"]',
        title: 'Tìm kiếm nhanh',
        description:
            'Tìm kiếm nhân viên theo:\n• Tên nhân viên\n• Mã nhân viên\n• Email\n• Số điện thoại',
    },
    {
        selector: '[data-tour="employee-settings"]',
        title: 'Cài đặt bảng',
        description:
            'Tuỳ chỉnh hiển thị bảng:\n• Cỡ chữ (Nhỏ/Vừa/Lớn)\n• Chiều cao hàng\n• Ẩn/hiện cột\n• Export Excel',
    },
]

/**
 * Tour hook for Employee List page
 * Shows key features of the employee management interface
 */
export const useEmployeeListTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useEmployeeListTour
